/**
 * Minimal .xlsx -> CSV conversion, done in the browser so admins can upload a vendor's raw
 * spreadsheet (TeamFlect's "user upload template", etc.) without saving it as CSV first. Only
 * needs what those exports contain: one worksheet, shared strings, inline strings, and plain
 * cell values. `fflate` unzips (xlsx is a zip); the sheet XML is parsed with small regexes —
 * no heavy spreadsheet library.
 */
import { unzipSync, strFromU8 } from 'fflate'

function unescapeXml(value: string): string {
  return value
    .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec) => String.fromCodePoint(parseInt(dec, 10)))
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&')
}

/** [A-Z]+ column letters from a cell ref like "AB12" -> 0-based index */
function columnIndex(ref: string): number {
  const letters = /^([A-Z]+)/.exec(ref)?.[1]
  if (!letters) return 0
  let n = 0
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64)
  return n - 1
}

function parseSharedStrings(xml: string): string[] {
  if (!xml) return []
  const out: string[] = []
  for (const si of xml.matchAll(/<si\b[^>]*>([\s\S]*?)<\/si>/g)) {
    let text = ''
    for (const t of si[1].matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)) text += unescapeXml(t[1])
    out.push(text)
  }
  return out
}

function parseSheet(xml: string, shared: string[]): string[][] {
  const rows: string[][] = []
  for (const rowMatch of xml.matchAll(/<row\b[^>]*>([\s\S]*?)<\/row>/g)) {
    const cells: string[] = []
    const cellRegex = /<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g
    for (const c of rowMatch[1].matchAll(cellRegex)) {
      const attrs = c[1]
      const body = c[2] ?? ''
      const ref = /r="([A-Z]+\d+)"/.exec(attrs)?.[1] ?? ''
      const type = /t="([^"]*)"/.exec(attrs)?.[1] ?? ''
      let value = ''
      if (type === 's') {
        const idx = /<v>([\s\S]*?)<\/v>/.exec(body)?.[1]
        value = idx !== undefined ? shared[Number(idx)] ?? '' : ''
      } else if (type === 'inlineStr') {
        for (const t of body.matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)) value += unescapeXml(t[1])
      } else {
        const v = /<v>([\s\S]*?)<\/v>/.exec(body)?.[1]
        value = v !== undefined ? unescapeXml(v) : ''
      }
      cells[columnIndex(ref)] = value
    }
    rows.push(cells)
  }
  return rows
}

function csvField(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
}

function tableToCsv(rows: string[][]): string {
  // Drop fully-empty trailing rows and normalise each row to the widest one.
  const width = rows.reduce((max, r) => Math.max(max, r.length), 0)
  const cleaned = rows.filter(r => r.some(c => c !== undefined && c !== ''))
  return cleaned.map(r => Array.from({ length: width }, (_, i) => csvField(r[i] ?? '')).join(',')).join('\r\n')
}

export function xlsxBytesToCsv(bytes: Uint8Array): string {
  const zip = unzipSync(bytes)
  const sharedXml = zip['xl/sharedStrings.xml'] ? strFromU8(zip['xl/sharedStrings.xml']) : ''
  const shared = parseSharedStrings(sharedXml)

  const sheetName = Object.keys(zip)
    .filter(name => /^xl\/worksheets\/sheet\d+\.xml$/.test(name))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))[0]
  if (!sheetName) throw new Error('xlsx: no worksheet found')
  return tableToCsv(parseSheet(strFromU8(zip[sheetName]), shared))
}

export async function xlsxToCsv(file: File): Promise<string> {
  return xlsxBytesToCsv(new Uint8Array(await file.arrayBuffer()))
}

/** True for a spreadsheet upload we should convert client-side before sending to the CSV endpoint. */
export function isXlsx(file: File): boolean {
  return /\.xlsx$/i.test(file.name) || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
}
